function parseHex(hex: string): [number, number, number] {
  const cleaned = hex.replace('#', '');
  const fullHex =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map(c => c + c)
          .join('')
      : cleaned;
  return [
    parseInt(fullHex.slice(0, 2), 16),
    parseInt(fullHex.slice(2, 4), 16),
    parseInt(fullHex.slice(4, 6), 16),
  ];
}

/**
 * Converts a hex color to rgba format.
 * Supports both 3-char (#fff) and 6-char (#ffffff) hex codes.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Extracts comma-separated RGB channels from a hex color for use with CSS variables.
 * E.g., '#7FCFFF' → '127, 207, 255', enabling `rgba(var(--color-rgb), 0.2)`.
 */
export function hexToRgbChannels(hex: string): string {
  const [r, g, b] = parseHex(hex);
  return `${r}, ${g}, ${b}`;
}

/** WCAG 2.1 relative luminance. */
function luminance(hex: string): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** WCAG 2.1 contrast ratio. */
function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/**
 * The more readable of `preferred` and white against `surface`. Non-hex values (`var(...)`, colour
 * functions) keep the caller's choice.
 */
export function readableTextOn({
  surface,
  preferred,
}: {
  surface: string;
  preferred: string;
}): string {
  if (!HEX_RE.test(surface) || !HEX_RE.test(preferred)) return preferred;
  return contrastRatio(surface, '#ffffff') > contrastRatio(surface, preferred)
    ? '#ffffff'
    : preferred;
}

/**
 * Whether a hex colour clears WCAG 1.4.11's 3:1 non-text minimum against a white surface, i.e.
 * whether it can be used as an icon or border on a light background. Returns false for anything
 * that isn't a plain hex, so `var(...)` and colour functions fall through to their caller's
 * default rather than being silently accepted.
 */
export function isReadableOnLight(color: string): boolean {
  return HEX_RE.test(color) && contrastRatio(color, '#ffffff') >= 3;
}
